/**
 * The shell's whole job: hand inputs to the core, execute what it returns.
 *
 * Story V2-091. `voice_server_lld_v2.md` §19.2, §19.3.
 *
 * # Why this is a plain function and not a hook
 *
 * Everything it touches is injected — the core, the transport factory, the
 * devices, the timer and the clock — so `node --test` can drive a whole
 * call, including a dropped connection and the resume after it, with no
 * browser and no WebAssembly. The hook around it (`useVoiceCall.js`) is
 * fifteen lines of React and has no logic to test.
 *
 * # What is deliberately not here
 *
 * No sequence number, no resume token, no generation, no turn state and no
 * reconnect schedule. All of those are in `voice-client-core`, and a copy
 * here would be the second implementation §19 exists to prevent. The test
 * `noState.test.mjs` greps this directory for them.
 */

/**
 * @typedef {{
 *   opened: () => object[],
 *   control: (text: string) => object[],
 *   media: (bytes: Uint8Array) => object[],
 *   captured: (bytes: Uint8Array) => object[],
 *   text: (text: string) => object[],
 *   setMuted: (muted: boolean) => object[],
 *   hangUp: (reason: string) => object[],
 *   transportLost: () => object[],
 *   tick: (nowMs: number) => object[],
 *   drained: (bytes: number) => object[],
 *   state: () => string,
 * }} Core the WASM binding, or a fake with the same shape
 */

/**
 * @typedef {{
 *   send: (frame: string | Uint8Array) => void,
 *   close: () => void,
 * }} Transport
 */

/** How often the core is ticked while a call is up. */
export const TICK_MS = 250;

/**
 * Whether to narrate the call to the console.
 *
 * `?trace=1`, or `localStorage.voiceTrace = "1"` to survive a reload. Off by
 * default and not a build flag: the calls worth tracing are the ones
 * happening on somebody else's machine, and asking them to run a different
 * build is asking them to reproduce a different program.
 *
 * What it prints is sizes, counts and kinds — never the audio and never a
 * transcript (`AGENTS.md` §I.15 applies to a browser console as much as to
 * a log file, and a console is the easier of the two to paste somewhere).
 */
export function tracer() {
  return tracing()
    ? (what, detail) => console.log(`[voice] ${what}`, detail ?? "")
    : () => {};
}

/** Whether tracing is asked for. [`tracer`] is what callers want. */
function tracing() {
  try {
    const asked = new URLSearchParams(globalThis.location?.search ?? "").get(
      "trace",
    );
    return asked === "1" || globalThis.localStorage?.voiceTrace === "1";
  } catch {
    // No `location`, no `localStorage`, or a browser that refuses storage
    // in this context. Not tracing is always a safe answer.
    return false;
  }
}

/**
 * Runs one call.
 *
 * @param {{
 *   core: Core,
 *   connect: (handlers: {
 *     onControl: (text: string) => void,
 *     onMedia: (bytes: Uint8Array) => void,
 *     onOpen: () => void,
 *     onClose: () => void,
 *   }) => Transport,
 *   devices: {
 *     capture: (on: boolean) => void | Promise<void>,
 *     play: (bytes: Uint8Array) => void,
 *     flush: () => void,
 *   },
 *   onState: (state: object) => void,
 *   onEnded: (ended: { code: string, reason: string }) => void,
 *   schedule?: (fn: () => void, ms: number) => unknown,
 *   cancel?: (handle: unknown) => void,
 *   now?: () => number,
 * }} wiring
 */
export function createCall({
  core,
  connect,
  devices,
  onState,
  onEnded,
  schedule = setTimeout,
  cancel = clearTimeout,
  now = () => performance.now(),
}) {
  /** @type {Transport | null} */
  let transport = null;
  let ticker = null;
  let closed = false;
  /**
   * Which transport is current.
   *
   * A socket that has been replaced can still deliver a close — the real
   * one detaches its handlers, but a shell should not depend on that, and
   * two resume schedules for one loss is two reconnects where the second
   * arrives after the first has already been welcomed.
   */
  let epoch = 0;

  const trace = tracer();

  /** Frames and bytes each way, so a trace says whether audio is moving. */
  const counts = { played: 0, playedBytes: 0, sent: 0, sentBytes: 0 };

  const run = (commands) => {
    for (const command of commands) {
      apply(command);
    }
  };

  const apply = (command) => {
    switch (command.kind) {
      case "send_control":
        // A transport that has gone is not an error here: the core has
        // already been told, and it schedules the resume. Dropping the
        // frame is what the protocol expects — the server replays control
        // after a resume, and the client re-sends its `hello`.
        trace("send_control", command.text.slice(0, 200));
        transport?.send(command.text);
        break;
      case "send_media":
        counts.sent += 1;
        counts.sentBytes += command.bytes.length;
        // Every fiftieth, which is one a second: a line per uplink frame
        // buries everything else in the console.
        if (counts.sent % 50 === 1) {
          trace("uplink", {
            frames: counts.sent,
            totalBytes: counts.sentBytes,
          });
        }
        transport?.send(command.bytes);
        break;
      case "capture":
        trace("capture", { on: command.on });
        devices.capture(command.on);
        break;
      case "play":
        counts.played += 1;
        counts.playedBytes += command.bytes.length;
        trace("play", {
          bytes: command.bytes.length,
          frames: counts.played,
          totalBytes: counts.playedBytes,
        });
        devices.play(command.bytes);
        break;
      case "flush_playback":
        // Rare, and it discards audio the caller was about to hear — so it
        // is never merely traced away with the frames.
        trace("flush_playback", { discardingAfter: counts.playedBytes });
        devices.flush();
        break;
      case "reconnect":
        trace("reconnect", { afterMs: command.afterMs });
        reconnect(command.afterMs);
        break;
      case "ended":
        trace("ended", { code: command.code, reason: command.reason });
        finish(command);
        break;
      case "published": {
        const state = JSON.parse(core.state());
        // The jitter counters are the client's own record of audio it threw
        // away, and until they were published nothing could see it.
        trace("state", {
          session: state.session,
          connection: state.connection?.kind ?? state.connection,
          jitter: state.metrics?.jitter,
        });
        onState(state);
        break;
      }
      default:
        // A command this build does not know, from a core that is newer
        // than the page. Ignored rather than thrown: the page and the WASM
        // are built together, so this is unreachable, and throwing from a
        // socket callback would end a call over it.
        break;
    }
  };

  const open = () => {
    if (closed) {
      return;
    }
    epoch += 1;
    const mine = epoch;
    // Every handler is gated on being the current transport's, not just
    // the close: a frame from a replaced socket is a frame the core has
    // already moved past, and feeding it one would put a stale `welcome`
    // over a fresh one.
    const current = () => !closed && mine === epoch;
    transport = connect({
      onOpen: () => current() && run(core.opened()),
      onControl: (text) => {
        trace("recv_control", text.slice(0, 200));
        return current() && run(core.control(text));
      },
      onMedia: (bytes) => current() && run(core.media(bytes)),
      onClose: () => current() && run(core.transportLost()),
    });
  };

  const reconnect = (afterMs) => {
    // The old transport is finished (§19.3). Closed before the timer, not
    // after: a socket left open through an eight-second backoff is a socket
    // still delivering frames the core has already moved past.
    transport?.close();
    transport = null;
    // Retired here, so anything the old socket delivers after this point
    // fails `current()` above.
    epoch += 1;
    if (!closed) {
      schedule(open, afterMs);
    }
  };

  const finish = (ended) => {
    closed = true;
    if (ticker !== null) {
      cancel(ticker);
      ticker = null;
    }
    transport?.close();
    transport = null;
    devices.capture(false);
    devices.flush();
    onEnded({ code: ended.code, reason: ended.reason });
  };

  const tick = () => {
    if (closed) {
      return;
    }
    run(core.tick(now()));
    ticker = schedule(tick, TICK_MS);
  };

  open();
  ticker = schedule(tick, TICK_MS);

  return {
    /** The microphone produced a frame. */
    captured: (bytes) => run(core.captured(bytes)),
    /** The speaker consumed some of what it was given. */
    drained: (bytes) => run(core.drained(bytes)),
    /** The user typed into the call. */
    say: (text) => run(core.text(text)),
    /** The user muted or unmuted their microphone. */
    setMuted: (muted) => run(core.setMuted(muted)),
    /**
     * The user hung up. The core answers with `bye` and then `ended`, so the
     * frame is written before `finish` closes the socket (§19.3). After the
     * call is over this does nothing: a page unmounting after the button
     * was pressed is the ordinary case.
     */
    hangUp: (reason = "user_hung_up") => {
      if (closed) {
        return;
      }
      run(core.hangUp(reason));
    },
    /** Whether the call is over. */
    isClosed: () => closed,
  };
}

/**
 * Opens the voice socket.
 *
 * `voice_server_lld_v2.md` §9.1: the credential rides in the subprotocol
 * list, because a `WebSocket` constructor takes a URL and a list of names
 * and nothing else. The server selects the *version*, never the credential.
 *
 * Which agent answers is the route's, fixed when the host mounted it
 * (`voice_agent_hld_v2.md` §17), and which conversation to join is
 * `hello.session` — so there is nothing for this URL to carry but the path,
 * and `settings/endpoints.js` is where that one decision already lives.
 *
 * @param {{ endpoint: string, token: string }} config
 */
export function voiceTransport({ endpoint, token }) {
  const url = endpoint;

  return (handlers) => {
    const socket = new WebSocket(url, ["voice.v2", `bearer.${token}`]);
    socket.binaryType = "arraybuffer";
    socket.onopen = () => handlers.onOpen();
    socket.onmessage = (event) => {
      if (typeof event.data === "string") {
        handlers.onControl(event.data);
      } else {
        handlers.onMedia(new Uint8Array(event.data));
      }
    };
    // Both, because a socket that errors also closes, and one that closes
    // without erroring is the ordinary case. The core is idempotent about
    // being told twice; the guard in `onClose` above is what makes it so.
    socket.onclose = () => handlers.onClose();
    socket.onerror = () => handlers.onClose();

    return {
      send: (frame) => {
        // `readyState` and not a try/catch: sending on a CONNECTING socket
        // throws, and the core's first frame races the open on a fast LAN.
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(frame);
        }
      },
      close: () => {
        socket.onclose = null;
        socket.onerror = null;
        socket.close();
      },
    };
  };
}
